import React, { useState } from 'react';
import './ResultCard.css';
import { useSelector } from 'react-redux';
import WhatsAppLogo from '../../assets/WhatsApp.svg';
import ResultCardAlert from './ResultCardAlert';

// Helper function to get full airport name
const getAirportName = (code, airports) => {
  const airport = airports.find(a => a.code === code && !a.isRegion);
  return airport ? airport.name : code;
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).replace(/(\d+)/, '$1' + getOrdinalSuffix(date.getDate()));
};

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc)
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// Helper function to get full board basis description
const getBoardBasisDescription = (code) => {
  const boardTypes = {
    'AI': 'All-Inclusive',
    'AP': 'All-Inclusive Plus',
    'BB': 'Bed & Breakfast',
    'CB': 'Continental Breakfast',
    'CC': 'Catered Chalet',
    'CL': 'Club Board',
    'DH': 'Deluxe Half Board',
    'DI': 'Drinks Inclusive',
    'FB': 'Full Board',
    'FD': 'Fly Drive',
    'FP': 'Full Board Plus',
    'HB': 'Half Board',
    'NA': 'n/a',
    'RO': 'Room Only',
    'SC': 'Self Catering',
    'VB': 'Board varies-as text'
  };
  return boardTypes[code] || code;
};

/**
 * ResultCard Component
 * 
 * Displays comprehensive information about a holiday package including:
 * - Basic details (hotel, location, dates, etc.)
 * - Total price information
 * - WhatsApp alerts functionality
 * - Claiva AI analysis integration
 * 
 * @param {Object} packageHoliday - Complete holiday package data object
 */
const ResultCard = ({ packageHoliday }) => {
  const [showAlert, setShowAlert] = useState(false);
  const airports = useSelector((state) => state.search.airports);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatPrice = (price) => {
    return `£${Math.round(price)}`;
  };

  const brandLogo = require(`../../assets/${packageHoliday.Brand}.svg`);
  const mainImage = packageHoliday.HotelDetails.HotelImages[0];
  const location = `${packageHoliday.HolidayResort}, ${packageHoliday.HolidayRegion}, ${packageHoliday.HolidayCountry}`;

  return (
    <div 
      className="result-card"
      data-item-key={packageHoliday.ItemKey}
      data-hotel-name={packageHoliday.HotelDetails?.HotelName}
      data-total-price={packageHoliday.PriceDetails?.TotalPrice}
    >
      <div className="result-image">
        <img src={mainImage} alt={packageHoliday.HotelDetails.HotelName} />
      </div>

      <div className="result-content">
        <h2>
          {packageHoliday.HotelDetails.HotelName}
          {' '}
          {'★'.repeat(packageHoliday.HotelDetails.StarRating)}
        </h2>
        <p className="location">{location}</p>
        
        <div className="result-details-container">
          <span className="detail-item">
            <span className="material-icons">calendar_today</span> 
            {formatDate(packageHoliday.FlightDetails.Outbound.DepartureDateTime)}
          </span>
          <span className="detail-item">
            <span className="material-icons">nights_stay</span> 
            {packageHoliday.Duration} nights
          </span>
          <span className="detail-item">
            <span className="material-icons">restaurant</span> 
            {getBoardBasisDescription(packageHoliday.BoardBasis)}
          </span>
          
          {packageHoliday.FlightDetails.Outbound && (
            <>
              <span className="detail-item">
                <span className="material-icons">flight_takeoff</span>
                {getAirportName(packageHoliday.FlightDetails.Outbound.DepartureAirportCode, airports)}
              </span>
              <span className="detail-item">
                <span className="material-icons">airlines</span>
                {packageHoliday.FlightDetails.Outbound.AirlineName}
              </span>
              <span className="detail-item">
                <span className="material-icons">schedule</span>
                {formatDuration(packageHoliday.FlightDetails.Outbound.FlightDuration)}
              </span>
            </>
          )}
        </div>

        <div className="price-and-button">
          <div className="price-container">
            <img 
              src={brandLogo} 
              alt={`${packageHoliday.Brand} logo`} 
              className="brand-logo"
            />
            <div className="price-column">
              <div className="price">
                {formatPrice(packageHoliday.PriceDetails.TotalPrice)}
              </div>
            </div>
            <button 
              className="visit-site-button" 
              onClick={() => window.open(packageHoliday.DeeplinkUrl, '_blank')}
            >
              Visit site
            </button>
          </div>
        </div>

        <div className="price-alert-container">
          {/* Claiva Price Analysis Placeholder */}
          <div 
            data-claiva-placeholder-id={packageHoliday.ItemKey}
            className="claiva-price-analysis"
          >
            {/* Claiva analysis will be injected here when service is available */}
          </div>
          
          <button 
            className="price-alert-button"
            onClick={() => setShowAlert(true)}
          >
            <img 
              src={WhatsAppLogo} 
              alt="WhatsApp" 
              className="whatsapp-logo-small"
            />
            Set up price alerts
          </button>
        </div>
      </div>

      {showAlert && (
        <ResultCardAlert 
          packageHoliday={packageHoliday} 
          onClose={() => setShowAlert(false)} 
        />
      )}
    </div>
  );
};

export default ResultCard;